import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Papa from 'papaparse';
import { getItem, setItem } from 'utils/LocalStorage';
import { isEmpty } from 'lodash';
import {
  createProductOnSitefinity,
  abortAllPendingRequests,
  wasRequestCancelled,
} from 'apiConsumers/SitefinityAPI';
import ImportProduct from 'components/ImportProduct';
import { inspect } from 'util';

const localStorageKey = 'importProducts';
const LEAVE_MESSAGE = 'Leaving this page will pause the products creation'; // Only IE supports it

class ImportProductContainer extends Component {
  constructor(props) {
    super(props);
    const previousState = getItem(localStorageKey) || {};
    this.state = {
      processing: previousState.processing || false,
      completed: previousState.completed || false,
      products: previousState.products || [],
      processedProducts: previousState.processedProducts || 0,
      createdProducts: previousState.createdProducts || 0,
      failedProducts: previousState.failedProducts || 0,
      csvfile: undefined,
      showProgress: false,
      showErrorPanel: false,
    };
    this.saveProductsInState = this.saveProductsInState.bind(this);
    this.completeParsingProcess = this.completeParsingProcess.bind(this);
    this.leavingPage = this.leavingPage.bind(this);
    this.products = [];
    if (props.token) setItem('API_TOKEN', props.token);
  }

  componentDidMount() {
    window.addEventListener('beforeunload', this.leavingPage);
    window.addEventListener('unload', abortAllPendingRequests);
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.leavingPage);
    window.removeEventListener('unload', abortAllPendingRequests);
  }

  leavingPage = (e) => {
    e.preventDefault();
    const { processing, showProgress, completed } = this.state;
    // Only show the message if your are currently importing products, otherwise, just reload the page
    if (processing && showProgress && !completed) {
      e.returnValue = LEAVE_MESSAGE;
      return LEAVE_MESSAGE;
    }
    return null;
  };

  handleChange = (event) => {
    this.setState({
      csvfile: event.target.files[0],
    });
  };

  processCSV = (e) => {
    e.preventDefault();
    const { csvfile } = this.state;
    Papa.parse(csvfile, {
      step: this.saveProductsInState,
      complete: this.completeParsingProcess,
      header: true,
      encoding: 'ISO-8859-1', // Our files are created using MS Excel so this is the encoding for it
    });
  };

  createProducts = (e) => {
    const { products } = this.state;
    e.preventDefault();
    this.setState({
      showProgress: true,
    });
    products.map((product, index) => this.prepareAndSubmitProduct(product, index));
  };

  prepareAndSubmitProduct = (product, index) => {
    const { referer = '' } = this.props;
    if (!product.processed) {
      createProductOnSitefinity(product, referer)
        .then(() => {
          this.processAPIResponse(index, true);
        })
        .catch((error) => {
          // So when cancelling requests the product is no marked as processed with error
          if (!wasRequestCancelled(error)) {
            this.processAPIResponse(index, false, inspect(error.response || error.message || error));
          }
        });
    }
  }

  processAPIResponse = (index, success, error) => {
    this.setState((prev) => {
      const { products: prevProducts } = prev;
      const copiedPrevProducts = prevProducts.slice();
      copiedPrevProducts[index].processed = true;
      copiedPrevProducts[index].success = success;
      if (error) copiedPrevProducts[index].error = error;
      const persistedObject = {
        ...prev,
        products: [...copiedPrevProducts],
        processedProducts: prev.processedProducts + 1,
        completed: prev.products.length === prev.processedProducts + 1,
        processing: prev.products.length !== prev.processedProducts + 1,
      };
      if (success) persistedObject.createdProducts = prev.createdProducts + 1;
      else persistedObject.failedProducts = prev.failedProducts + 1;
      setItem(localStorageKey, { ...persistedObject });
      return { ...persistedObject };
    });
  }

  cancelProcess = (e) => {
    e.preventDefault();
    this.setState({
      processing: false,
      completed: false,
      products: [],
      processedProducts: 0,
      createdProducts: 0,
      failedProducts: 0,
      csvfile: undefined,
      showProgress: false,
    }, () => setItem(localStorageKey, {}));
  };

  completeParsingProcess = () => {
    this.setState({
      processing: true,
      completed: false,
      processedProducts: 0,
      createdProducts: 0,
      failedProducts: 0,
      products: this.products,
    }, () => setItem(localStorageKey, { ...this.state }));
  }

  saveProductsInState = (results = {}) => {
    const { data, errors } = results;
    // Filtering the products that has no title so we can avoid processing empty ones
    // that paparse created based on new lines
    if (isEmpty(errors) && data.Title) this.products.push(({ ...data, processed: false }));
  }

  toggleErrorPanel = (e) => {
    e.preventDefault();
    this.setState((prev) => ({ showErrorPanel: !prev.showErrorPanel }));
  };

  render() {
    const { token } = this.props;
    const {
      processing,
      completed,
      products,
      showProgress,
      createdProducts,
      failedProducts,
      csvfile,
      showErrorPanel,
    } = this.state;

    if (!token) {
      return null;
    }

    return (
      <ImportProduct
        title='Create products using a .csv file'
        processing={processing}
        completed={completed}
        products={products}
        showProgress={showProgress}
        createdProducts={createdProducts}
        failedProducts={failedProducts}
        csvfile={csvfile}
        cancelProcess={this.cancelProcess}
        createProducts={this.createProducts}
        handleChange={this.handleChange}
        processCSV={this.processCSV}
        showErrorPanel={showErrorPanel}
        toggleErrorPanel={this.toggleErrorPanel}
      />
    );
  }
}

ImportProductContainer.propTypes = {
  token: PropTypes.string.isRequired,
  referer: PropTypes.string,
};

export default ImportProductContainer;
